import { parseWithZod } from '@conform-to/zod'
import { Prisma } from '@prisma/client'
import { type ActionFunctionArgs, json, redirect } from '@remix-run/node'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { requireUserId } from '#app/utils/auth.server.js'
import { prisma } from '#app/utils/db.server.js'

export const OwnedUpdateSchema = z.object({
	ownedUpdateAction: z.union([z.literal('add'), z.literal('remove')]),
	productId: z.string(),
})

export async function action({ request }: ActionFunctionArgs) {
	const userId = await requireUserId(request)
	const formData = await request.formData()

	const submission = await parseWithZod(formData, {
		schema: OwnedUpdateSchema.superRefine(async (data, ctx) => {
			if (!data.ownedUpdateAction) return

			if (
				data.ownedUpdateAction !== 'add' &&
				data.ownedUpdateAction !== 'remove'
			) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Invalid action',
				})
			}
		}),
		async: true,
	})

	if (submission.status !== 'success') {
		return json(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { ownedUpdateAction, productId } = submission.value

	await prisma.$transaction(async prisma => {
		if (ownedUpdateAction === 'add') {
			const existingWishlistProduct = await prisma.wishlistProduct.findUnique({
				where: {
					userId_productId: {
						userId,
						productId,
					},
				},
			})

			if (existingWishlistProduct) {
				await prisma.wishlistProduct
					.delete({
						where: {
							userId_productId: {
								userId,
								productId,
							},
						},
					})
					.catch(error => {
						if (error instanceof Prisma.PrismaClientKnownRequestError) {
							if (error.code === 'P2025') {
								console.log(
									'Wishlist product record not found, nothing to delete.',
								)
							} else {
								console.error('Error deleting wishlist product:', error)
								throw error
							}
						}
					})
			}

			await prisma.ownedProduct
				.create({
					data: {
						userId,
						productId,
					},
				})
				.catch(error => {
					if (error.code === 'P2002') {
						console.log('Product already exists in owned products.')
					}
				})
		} else if (ownedUpdateAction === 'remove') {
			await prisma.ownedProduct
				.delete({
					where: {
						userId_productId: {
							userId,
							productId,
						},
					},
				})
				.catch(error => {
					if (error instanceof Prisma.PrismaClientKnownRequestError) {
						if (error.code === 'P2025') {
							console.log('Owned product record not found, nothing to delete.')
						} else {
							console.error('Error deleting wishlist product:', error)
							throw error
						}
					}
				})
		}
	})

	return redirect('..')
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
